import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Controller } from "react-hook-form";

import { displayFlex } from "../../styles/mui/styles/display";
import { FormInputProps } from "./FormInputProps";
import { isNil } from "lodash";
import colors from "../../styles/colors/colors";
import fontWeight from "../../styles/mui/fontWeight";
import { getValue } from "../../utils";
import { DropdownOption, DropdownOptionValue } from "./constant/FormComponents";
import { GenderInputUI, OddEvenDropdownStyle } from "../styles/styles";

export const FormInputDropdown: React.FC<FormInputProps> = ({
  control,
  disabled,
  helper,
  label,
  list,
  name,
  onChangeHandler,
  mandatorySymbol,
  readOnly,
  InputStyle,
  oddEvenStyle
}) => {
  const selectRef = useRef(null);
  const [hideMenu, setHideMenu] = useState(false);
  const checkInitialData = (data: any) =>
    Array.isArray(data) && data.length > 0 && data[0].label !== "";
  const getMenuItemValue = (value: DropdownOptionValue): any => {
    if (value === null || value === undefined) {
      return "";
    }

    return value;
  };
 const isRequired =
  typeof helper?.required === "object" &&
  helper.required !== null &&
  "value" in helper.required
    ? Boolean(helper.required.value)
    : Boolean(helper?.required);


  const generateSingleOptions = (list: DropdownOption[]) => {
    if (!checkInitialData(list)) return [];
    return list.map((option , index) => (
      <MenuItem
        key={`${option.value}-${option.label}`}
        value={getMenuItemValue(option.value)}
        disabled={option.disabled || false}
        sx={oddEvenStyle? OddEvenDropdownStyle(Number(index)) : {}}
        data-testid={`${option.value}-${option.label}`}
      >
        {option.label}
      </MenuItem>
    ));
  };
  if (!list) return null;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setHideMenu(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    if (selectRef.current) {
      observer.observe(selectRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <FormControl variant="standard" size={"small"} sx={GenderInputUI}>
      {label && (
        <InputLabel
          required={isRequired}
          id={`${name}-label`}
          shrink={true}
          sx={displayFlex}
        >
          <Box sx={displayFlex}>
            <Typography
              variant="h5"
              fontWeight={fontWeight.Weight[3]}
              color={colors.fonts[2]}
              lineHeight={"0.95rem"}
            >
              {label}
            </Typography>
            {!getValue(helper, "required.value", "") && mandatorySymbol && (
              <Typography
                variant="h3"
                fontWeight={fontWeight.Weight[3]}
                color={colors.red[100]}
                pl={"0.4rem"}
              >
                *
              </Typography>
            )}
          </Box>
        </InputLabel>
      )}
      <Controller
        defaultValue={""}
        render={({
          field: { onChange, value, ref, name },
          fieldState: { error },
        }) => {
          return (
            <>
              <Select
                required={isRequired}
                ref={selectRef}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  disableScrollLock: true,
                  slotProps: {
                    paper: {
                      style: {
                        maxHeight: 250,
                        display: hideMenu ? "none" : "block",
                      },
                    },
                  },
                }}
                name={name}
                error={error !== undefined}
                labelId={`${name}-label`}
                id={`${name}-select`}
                onChange={(e) => {
                  onChangeHandler && onChangeHandler(e);
                  onChange(e);
                }}
                value={!isNil(value) && value !== 0 ? value : ""}
                sx={InputStyle}
                inputRef={ref}
                fullWidth
                disabled={disabled}
                readOnly={readOnly}
                displayEmpty
                data-testid={name}
                inputProps={{ 'data-testid': `${name}-test` }}
              >
                <MenuItem value= "">
                  All
                </MenuItem>
                {generateSingleOptions(list)}
              </Select>
              {isRequired && (
                <FormHelperText>{error ? error.message : null}</FormHelperText>
              )}
            </>
          );
        }}
        control={control}
        name={name}
        rules={helper}
      />
    </FormControl>
  );
};
